/**
 * Safe localStorage wrapper that handles cases where localStorage is unavailable
 * (e.g., Safari private browsing mode, browsers with cookies disabled)
 */

/**
 * Checks if localStorage is available and accessible
 */
function isLocalStorageAvailable(): boolean {
  try {
    if (typeof window === 'undefined') {
      return false;
    }

    // Check if localStorage exists and is not null
    if (!window.localStorage) {
      return false;
    }

    // Test if we can actually use it (Safari private mode returns an object but throws on use)
    const testKey = '__localStorage_test__';
    window.localStorage.setItem(testKey, 'test');
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Safely get an item from localStorage
 * Returns null if localStorage is unavailable or the key doesn't exist
 */
export function safeGetItem(key: string): string | null {
  try {
    if (!isLocalStorageAvailable()) {
      return null;
    }
    return window.localStorage.getItem(key);
  } catch (e) {
    console.warn(`Failed to get item from localStorage: ${key}`, e);
    return null;
  }
}

/**
 * Safely set an item in localStorage
 * Silently fails if localStorage is unavailable
 */
export function safeSetItem(key: string, value: string): void {
  try {
    if (!isLocalStorageAvailable()) {
      return;
    }
    window.localStorage.setItem(key, value);
  } catch (e) {
    console.warn(`Failed to set item in localStorage: ${key}`, e);
  }
}

/**
 * Safely remove an item from localStorage
 * Silently fails if localStorage is unavailable
 */
export function safeRemoveItem(key: string): void {
  try {
    if (!isLocalStorageAvailable()) {
      return;
    }
    window.localStorage.removeItem(key);
  } catch (e) {
    console.warn(`Failed to remove item from localStorage: ${key}`, e);
  }
}
